import { CommonModule } from '@angular/common';
import {
    Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges,
} from '@angular/core';
import {
    FormControl, ReactiveFormsModule, ValidatorFn, Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

import { UserDataPickerComponent } from '../selectors/user-data-picker/user-data-picker.component';
import { UserDataService } from '../../services/user-data/user-data.service';

/**
 * Single-widget editor for the rightmost column of the schema-column-browser.
 *
 * <p>Given one leaf's schema and value, picks the right Material widget
 * (mat-input / mat-select / mat-slide-toggle / mat-chip-grid / picker) and
 * emits scalar value changes via {@code (valueChange)}. The browser writes
 * the emitted scalar at the column's full JSON path.
 *
 * <p>Read-only leaves render the widget in a disabled state with a lock
 * affordance. The trash button (when value is non-empty) emits
 * {@code (pathCleared)} so the host's deleteAtPath handles JSON removal.
 */
@Component({
    selector: 'app-schema-leaf-editor',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatSelectModule, MatSlideToggleModule,
        MatAutocompleteModule, MatButtonModule, MatChipsModule, MatIconModule, MatTooltipModule,
        UserDataPickerComponent,
    ],
    templateUrl: './schema-leaf-editor.component.html',
    styleUrl: './schema-leaf-editor.component.css',
})
export class SchemaLeafEditorComponent implements OnChanges {

    @Input({ required: true }) schema!: any;
    @Input() value: any = null;
    /** Full JSON path of this leaf, e.g. "$.location.city" — emitted with
     *  (pathCleared) so the host can deleteAtPath. */
    @Input() path: string = '$';
    @Input() disabled = false;

    @Output() valueChange = new EventEmitter<any>();
    @Output() pathCleared = new EventEmitter<string>();

    private readonly userDataService = inject(UserDataService);

    /** Single FormControl backing the widget. */
    control!: FormControl<any>;

    /** Chip separators for array-of-primitives editor. */
    readonly chipSeparators = [COMMA, ENTER];

    /** Autocomplete options, populated lazily on demand. */
    autocompleteOptions: string[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['schema'] || !this.control) {
            this.rebuildControl();
            this.prefetchAutocompleteIfNeeded();
        } else if (changes['value']) {
            // Apply external value updates without re-emitting valueChange
            // (avoids the browser bouncing it back at us).
            const v = this.value;
            if (this.control.value !== v) {
                this.control.setValue(v ?? this.defaultEmpty(), { emitEvent: false });
            }
        }
        if (changes['disabled'] && this.control) {
            this.isReadOnly ? this.control.disable() : this.control.enable();
        }
    }

    private rebuildControl(): void {
        const readOnly = this.isReadOnly;
        const initial = this.value ?? this.defaultEmpty();
        this.control = new FormControl<any>(
            { value: initial, disabled: readOnly },
            this.validatorsFor());
        this.control.valueChanges.subscribe(v => this.valueChange.emit(v));
    }

    private defaultEmpty(): any {
        const t = this.schema?.type;
        if (t === 'array') return [];
        if (t === 'boolean') return null;
        if (t === 'integer' || t === 'number') return null;
        return '';
    }

    private validatorsFor(): ValidatorFn[] {
        const v: ValidatorFn[] = [];
        const f = this.schema;
        if (!f) return v;
        if (typeof f.minLength === 'number') v.push(Validators.minLength(f.minLength));
        if (typeof f.maxLength === 'number') v.push(Validators.maxLength(f.maxLength));
        if (typeof f.minimum === 'number')   v.push(Validators.min(f.minimum));
        if (typeof f.maximum === 'number')   v.push(Validators.max(f.maximum));
        if (typeof f.pattern === 'string')   v.push(Validators.pattern(f.pattern));
        return v;
    }

    private prefetchAutocompleteIfNeeded(): void {
        const src = this.schema?.['x-options']?.source;
        if (typeof src !== 'string' || !src.startsWith('userdata-') || src === 'userdata') return;
        const handler = {
            onStart: () => {},
            onSuccess: (rows: any) => {
                this.autocompleteOptions = (rows ?? []).map((r: any) => String(r));
            },
            onError: () => { this.autocompleteOptions = []; },
            onComplete: () => {},
        };
        switch (src) {
            case 'userdata-types':           this.userDataService.getUserDataTypes(handler); break;
            case 'userdata-departments':     this.userDataService.getDepartments(handler); break;
            case 'userdata-org-units':       this.userDataService.getOrgUnits(handler); break;
            case 'userdata-financial-units': this.userDataService.getFinancialUnits(handler); break;
            default: this.autocompleteOptions = [];
        }
    }

    /** Live-filtered autocomplete results — same shape as SchemaForm. */
    filteredOptions(): string[] {
        const needle = String(this.control?.value ?? '').toLowerCase();
        if (!needle) return this.autocompleteOptions;
        return this.autocompleteOptions.filter(s => s.toLowerCase().includes(needle));
    }

    widget(): string {
        const t = this.schema?.type;
        if (t === 'boolean') return 'boolean';
        if (t === 'integer' || t === 'number') return 'number';
        if (t === 'array') return 'array';
        if (Array.isArray(this.schema?.enum)) return 'enum';
        const src = this.schema?.['x-options']?.source;
        if (src === 'userdata') return 'userdata-picker';
        if (typeof src === 'string' && src.startsWith('userdata-')) return 'autocomplete';
        if (src === 'connector-catalog') return 'connector-catalog';
        return 'text';
    }

    get isReadOnly(): boolean { return this.schema?.readOnly === true || this.disabled; }

    get label(): string { return this.schema?.title ?? this.lastPathSegment; }

    get description(): string { return this.schema?.description ?? ''; }

    get placeholder(): string {
        const examples = this.schema?.examples;
        return Array.isArray(examples) && examples.length > 0 ? String(examples[0]) : '';
    }

    get userDataPickerType(): string {
        return this.schema?.['x-options']?.filter?.type ?? '';
    }

    private get lastPathSegment(): string {
        const segs = this.path.split('.');
        const last = segs[segs.length - 1] ?? '$';
        // Strip "[N]" suffix on array-item paths.
        return last.replace(/\[\d+\]$/, '');
    }

    /** True when the underlying value is "present" (non-empty by our
     *  cleanseValue semantics — null / "" / [] / undefined are absent). */
    hasValue(): boolean {
        const v = this.control?.value;
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    }

    /** Trash icon — clear the leaf and emit pathCleared so the host deletes
     *  the JSON key directly. */
    clearValue(ev?: Event): void {
        ev?.stopPropagation();
        if (this.isReadOnly) return;
        this.control.setValue(this.defaultEmpty(), { emitEvent: false });
        this.pathCleared.emit(this.path);
    }

    // ── array-of-primitives helpers (chip-grid) ───────────────────────────

    arrayValue(): string[] {
        const v = this.control?.value;
        if (!Array.isArray(v)) return [];
        return v.map(item => item === null || item === undefined ? '' : String(item));
    }

    arrayAdd(event: MatChipInputEvent): void {
        const v = (event.value ?? '').trim();
        if (!v) return;
        const next = [...this.arrayValue(), v];
        this.control.setValue(next);
        event.chipInput?.clear();
    }

    arrayRemove(item: string): void {
        this.control.setValue(this.arrayValue().filter(x => x !== item));
    }
}
